import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../components/ui/dialog";
import {
  PenTool,
  Plus,
  Clock,
  CheckCircle2,
  Send,
  FileText,
  Users,
  ShieldCheck,
  Loader2,
  ArrowRight,
  Download,
  MousePointer2
} from "lucide-react";
import { toast } from "sonner";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function SignaturesPage() {
  const { user } = useAuth();
  const [sentSignatures, setSentSignatures] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProof, setSelectedProof] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      const [sentRes, pendingRes] = await Promise.all([
        api.get("/signatures/sent"),
        api.get("/signatures/pending")
      ]);
      setSentSignatures(sentRes.data);
      setPendingSignatures(pendingRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des signatures");
    } finally {
      setLoading(false);
    }
  };

  const viewProof = async (sigId) => {
    setLoadingProof(true);
    try {
      const response = await api.get(`/signatures/${sigId}/proof`);
      setSelectedProof(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Preuve non disponible");
    } finally {
      setLoadingProof(false);
    }
  };

  const exportProofPDF = async (sigId) => {
    try {
      const response = await api.get(`/signatures/${sigId}/proof/pdf`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Preuve_signature_${sigId}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Dossier de preuve téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const getRecipientStatus = (recipients) => {
    const signed = recipients.filter((r) => r.status === "signed").length;
    return `${signed}/${recipients.length}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="signatures-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Signatures</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Gérez vos demandes de signature électronique
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <Link to="/signatures/editor" data-testid="visual-editor-btn">
              <MousePointer2 className="w-4 h-4 mr-2" />
              Éditeur visuel
            </Link>
          </Button>
          <Button asChild className="btn-active w-full sm:w-auto">
            <Link to="/signatures/new" data-testid="new-signature-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande
            </Link>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pending" className="gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="pending-tab">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
            À signer ({pendingSignatures.length})
          </TabsTrigger>
          <TabsTrigger value="sent" className="gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="sent-tab">
            <Send className="w-3 h-3 sm:w-4 sm:h-4" />
            Envoyées ({sentSignatures.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Signatures */}
        <TabsContent value="pending" className="mt-6">
          {pendingSignatures.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-lg text-muted-foreground">
                  Aucune signature en attente
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingSignatures.map((sig) => (
                <Card key={sig.id} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                          <PenTool className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {sig.document_name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            De: {sig.sender_name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(sig.created_at)}
                          </p>
                          {sig.message && (
                            <p className="text-sm mt-2 text-muted-foreground italic">
                              "{sig.message}"
                            </p>
                          )}
                        </div>
                      </div>
                      <Button asChild className="btn-active">
                        <Link
                          to={`/signatures/${sig.id}/sign`}
                          data-testid={`sign-btn-${sig.id}`}
                        >
                          Signer
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sent Signatures */}
        <TabsContent value="sent" className="mt-6">
          {sentSignatures.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Send className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-lg text-muted-foreground">
                  Aucune demande envoyée
                </p>
                <Button asChild variant="link" className="mt-2">
                  <Link to="/signatures/new">Créer votre première demande</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sentSignatures.map((sig) => (
                <Card key={sig.id} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            sig.status === "completed"
                              ? "bg-green-100 dark:bg-green-900/30"
                              : "bg-blue-100 dark:bg-blue-900/30"
                          }`}
                        >
                          {sig.status === "completed" ? (
                            <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                          ) : (
                            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">
                              {sig.document_name}
                            </h3>
                            <Badge
                              variant="secondary"
                              className={
                                sig.status === "completed"
                                  ? "status-completed"
                                  : "status-pending"
                              }
                            >
                              {sig.status === "completed"
                                ? "Complété"
                                : "En attente"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Créé le {formatDate(sig.created_at)}
                          </p>

                          {/* Recipients */}
                          <div className="mt-4">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <Users className="w-4 h-4" />
                              Destinataires ({getRecipientStatus(sig.recipients)})
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {sig.recipients.map((recipient) => (
                                <Badge
                                  key={recipient.user_id}
                                  variant="outline"
                                  className={
                                    recipient.status === "signed"
                                      ? "border-green-500 text-green-600"
                                      : ""
                                  }
                                >
                                  {recipient.status === "signed" && (
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                  )}
                                  {recipient.full_name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {sig.status === "completed" && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => viewProof(sig.id)}
                            disabled={loadingProof}
                            data-testid={`view-proof-${sig.id}`}
                          >
                            <ShieldCheck className="w-4 h-4 mr-2" />
                            Voir la preuve
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => exportProofPDF(sig.id)}
                            data-testid={`export-proof-${sig.id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export PDF
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Proof Dialog */}
      <Dialog open={!!selectedProof} onOpenChange={() => setSelectedProof(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              Dossier de preuve cryptographique
            </DialogTitle>
          </DialogHeader>
          {selectedProof && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted">
                <h4 className="font-semibold mb-2">Hash du document</h4>
                <code className="text-xs break-all font-mono">
                  {selectedProof.document_hash}
                </code>
              </div>

              <div className="p-4 rounded-lg bg-muted">
                <h4 className="font-semibold mb-2">Hash de preuve</h4>
                <code className="text-xs break-all font-mono">
                  {selectedProof.proof_hash}
                </code>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Signatures</h4>
                <div className="space-y-3">
                  {selectedProof.signatures.map((sig, idx) => (
                    <div key={idx} className="p-4 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{sig.signer_name}</span>
                        <Badge variant="secondary" className="status-signed">
                          Signé
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {sig.signer_email}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(sig.signed_at)}
                      </p>
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">
                          Hash signature:
                        </p>
                        <code className="text-xs font-mono break-all">
                          {sig.signature_hash}
                        </code>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs text-muted-foreground text-center border-t pt-4">
                Créé le {formatDate(selectedProof.created_at)} - Conforme eIDAS
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
